// Context panels (docs/plans/transaction-wizard.md §7.1): a Visual / Dashboard
// / Report / record card beside a step, filtered by its values. The HOST draws
// the object (its own V / D / R renderers — the seam an `open` drawer uses);
// this only decides WHAT to draw, WHEN it re-runs and what a picked row does.
//
// A panel whose params do not all resolve is not drawn (no empty-chart noise);
// one the user may not view is hidden, never shown-then-refused.

import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { WizardContextPanel, WizardObjectSpec } from '../contract'
import { renderMap, resolvePath, text, type Row } from '../model'
import type { WizardController } from '../useWizard'
import { T, card, muted } from '../styles'

const OBJ_TYPE: Record<string, WizardObjectSpec['objType']> = {
  visual: 'V', dashboard: 'D', report: 'R', record: 'T',
}

function hash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

export function ContextPanel({ ctl, panel, layout }: {
  ctl: WizardController
  panel: WizardContextPanel
  layout?: WizardObjectSpec['layout']
}) {
  const { scope, host } = ctl
  const objType = OBJ_TYPE[panel.kind] || 'V'
  const [allowed, setAllowed] = useState<boolean | null>(host.canUse ? null : true)
  const [open, setOpen] = useState(panel.layout !== 'collapsed')
  useEffect(() => {
    if (!host.canUse) return
    let live = true
    host.canUse(objType, panel.obj_name, 'view')
      .then(ok => { if (live) setAllowed(ok) })
      .catch(() => { if (live) setAllowed(false) })
    return () => { live = false }
  }, [host, objType, panel.obj_name])

  const resolved = useMemo(() => {
    if (!scope) return null
    const params = renderMap(panel.params || {}, scope)
    const match = renderMap(panel.match || {}, scope)
    if (params.missing.length || match.missing.length) return null
    // refresh_on: the panel re-runs when one of these fields changes, after a
    // save and after a call returns (ctl.refreshKey).
    const sig = (panel.refresh_on || []).map(f => {
      const v = resolvePath(f, scope).value
      return `${f}=${v == null ? '' : String(v)}`
    }).join('|')
    return {
      params: params.values,
      match: match.values,
      refreshKey: hash(`${ctl.refreshKey}|${sig}|${JSON.stringify(params.values)}`),
    }
  }, [scope, panel, ctl.refreshKey])

  if (allowed === false || !resolved) return null
  const title = panel.title ? text(panel.title, scope!) : ''
  const effective = layout || (panel.layout === 'collapsed' ? 'panel' : panel.layout || 'panel')
  const spec: WizardObjectSpec = {
    objType,
    objName: panel.obj_name,
    params: resolved.params,
    ...(objType === 'T' ? { match: resolved.match } : {}),
    layout: effective,
    title: title || undefined,
    refreshKey: resolved.refreshKey,
    ...(panel.pick_row ? { onPickRow: (row: Row) => { void ctl.pickRow(panel, row) } } : {}),
  }
  const content = allowed === null ? null
    : host.renderObject ? host.renderObject(spec)
      : <div style={muted}>This view is not available here.</div>
  if (panel.layout === 'strip' && !layout) {
    return (
      <div data-tw-block={`context:${panel.obj_name}`} style={{
        border: `1px solid ${T.border}`, borderRadius: 8, background: T.band, padding: '6px 10px',
        fontSize: 14, overflowX: 'auto',
      }}>
        {content}
      </div>
    )
  }
  return (
    <div data-tw-block={`context:${panel.obj_name}`} style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
          border: 'none', borderBottom: open ? `1px solid ${T.border}` : 'none', background: T.band,
          color: T.text, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 40,
        }}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={{ flex: 1, textAlign: 'left' }}>{title || panel.obj_name}</span>
        {panel.pick_row && <span style={{ ...muted, fontWeight: 400 }}>tap a row to use it</span>}
      </button>
      {open && <div style={{ padding: 8, minHeight: 60 }}>{content}</div>}
    </div>
  )
}

/** The step's (or the `show` step's) panels in one placement. */
export default function ContextPanels({ ctl, panels, only }: {
  ctl: WizardController
  panels: WizardContextPanel[] | undefined
  /** 'strip' — the compact KPI lines; 'panels' — everything else. */
  only: 'strip' | 'panels'
}) {
  const list = (panels || []).filter(p => (only === 'strip' ? p.layout === 'strip' : p.layout !== 'strip'))
  if (!list.length) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {list.map((p, i) => <ContextPanel key={`${p.obj_name}-${i}`} ctl={ctl} panel={p} />)}
    </div>
  )
}
